/**
 * Hotel API Service using Amadeus API
 * 
 * This service makes direct requests to Amadeus Hotel API
 */

// Amadeus API Credentials
const AMADEUS_API_KEY = process.env.REACT_APP_AMADEUS_API_KEY || '0xbFHkrAUisc7PZab0Aq4T9moAEOhBwG';
const AMADEUS_API_SECRET = process.env.REACT_APP_AMADEUS_API_SECRET || 'Viqn4SEJGowvGSw8';
const AMADEUS_BASE_URL = 'https://test.api.amadeus.com'; // Test environment

// Token cache
let accessToken = null;
let tokenExpiry = null;

/**
 * Get Amadeus access token
 */
const getAccessToken = async () => {
  // Return cached token if still valid
  if (accessToken && tokenExpiry && Date.now() < tokenExpiry) {
    return accessToken;
  }

  try {
    const response = await fetch(`${AMADEUS_BASE_URL}/v1/security/oauth2/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'client_credentials',
        client_id: AMADEUS_API_KEY,
        client_secret: AMADEUS_API_SECRET,
      }),
    });

    if (!response.ok) {
      const errorData = await response.text();
      console.error('Token error:', errorData);
      throw new Error('Failed to get access token');
    }

    const data = await response.json();
    accessToken = data.access_token;
    // Set expiry 5 minutes before actual expiry for safety
    tokenExpiry = Date.now() + (data.expires_in - 300) * 1000;
    
    return accessToken;
  } catch (error) {
    console.error('Error getting access token:', error);
    throw error;
  }
};

/**
 * Popular cities database (fallback if API fails)
 */
const POPULAR_CITIES = [
  { name: 'Paris', cityCode: 'PAR', countryCode: 'FR', countryName: 'France', fullName: 'Paris, FR' },
  { name: 'New York', cityCode: 'NYC', countryCode: 'US', countryName: 'United States', fullName: 'New York, US' },
  { name: 'London', cityCode: 'LON', countryCode: 'GB', countryName: 'United Kingdom', fullName: 'London, GB' },
  { name: 'Dubai', cityCode: 'DXB', countryCode: 'AE', countryName: 'United Arab Emirates', fullName: 'Dubai, AE' },
  { name: 'Lagos', cityCode: 'LOS', countryCode: 'NG', countryName: 'Nigeria', fullName: 'Lagos, NG' },
  { name: 'Abuja', cityCode: 'ABV', countryCode: 'NG', countryName: 'Nigeria', fullName: 'Abuja, NG' },
  { name: 'Sydney', cityCode: 'SYD', countryCode: 'AU', countryName: 'Australia', fullName: 'Sydney, AU' },
  { name: 'Tokyo', cityCode: 'TYO', countryCode: 'JP', countryName: 'Japan', fullName: 'Tokyo, JP' },
  { name: 'Los Angeles', cityCode: 'LAX', countryCode: 'US', countryName: 'United States', fullName: 'Los Angeles, US' },
  { name: 'Chicago', cityCode: 'CHI', countryCode: 'US', countryName: 'United States', fullName: 'Chicago, US' },
  { name: 'Miami', cityCode: 'MIA', countryCode: 'US', countryName: 'United States', fullName: 'Miami, US' },
  { name: 'Toronto', cityCode: 'YYZ', countryCode: 'CA', countryName: 'Canada', fullName: 'Toronto, CA' },
  { name: 'Berlin', cityCode: 'BER', countryCode: 'DE', countryName: 'Germany', fullName: 'Berlin, DE' },
  { name: 'Rome', cityCode: 'ROM', countryCode: 'IT', countryName: 'Italy', fullName: 'Rome, IT' },
  { name: 'Barcelona', cityCode: 'BCN', countryCode: 'ES', countryName: 'Spain', fullName: 'Barcelona, ES' },
  { name: 'Amsterdam', cityCode: 'AMS', countryCode: 'NL', countryName: 'Netherlands', fullName: 'Amsterdam, NL' },
  { name: 'Singapore', cityCode: 'SIN', countryCode: 'SG', countryName: 'Singapore', fullName: 'Singapore, SG' },
  { name: 'Hong Kong', cityCode: 'HKG', countryCode: 'HK', countryName: 'Hong Kong', fullName: 'Hong Kong, HK' },
  { name: 'Bangkok', cityCode: 'BKK', countryCode: 'TH', countryName: 'Thailand', fullName: 'Bangkok, TH' },
  { name: 'Istanbul', cityCode: 'IST', countryCode: 'TR', countryName: 'Turkey', fullName: 'Istanbul, TR' },
  { name: 'Cairo', cityCode: 'CAI', countryCode: 'EG', countryName: 'Egypt', fullName: 'Cairo, EG' },
  { name: 'Johannesburg', cityCode: 'JNB', countryCode: 'ZA', countryName: 'South Africa', fullName: 'Johannesburg, ZA' },
  { name: 'Accra', cityCode: 'ACC', countryCode: 'GH', countryName: 'Ghana', fullName: 'Accra, GH' },
  { name: 'Nairobi', cityCode: 'NBO', countryCode: 'KE', countryName: 'Kenya', fullName: 'Nairobi, KE' },
];

/**
 * Search for cities by name using Amadeus API with fallback
 */
export const searchCities = async (query) => {
  try {
    if (!query || query.length < 2) {
      return {
        success: true,
        data: []
      };
    }
    
    const searchQuery = query.toLowerCase().trim();
    
    // First, try to match from popular cities
    const matchedCities = POPULAR_CITIES.filter(city => 
      city.name.toLowerCase().includes(searchQuery) ||
      city.fullName.toLowerCase().includes(searchQuery) ||
      city.countryName.toLowerCase().includes(searchQuery)
    );
    
    if (matchedCities.length > 0) {
      return {
        success: true,
        data: matchedCities.slice(0, 10)
      };
    }
    
    // Try Amadeus API
    try {
      const token = await getAccessToken();
      
      // Use Airport & City Search API
      const params = new URLSearchParams({
        keyword: query,
        subType: 'CITY',
        'page[limit]': '10'
      });

      const response = await fetch(`${AMADEUS_BASE_URL}/v1/reference-data/locations?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        
        // Transform to our format
        const cities = data.data?.map(location => ({
          name: location.name,
          cityCode: location.iataCode || location.address?.cityCode,
          countryCode: location.address?.countryCode,
          countryName: location.address?.countryName,
          fullName: `${location.name}, ${location.address?.countryCode || ''}`,
          type: location.subType
        })).filter(city => city.cityCode) || [];
        
        if (cities.length > 0) {
          return {
            success: true,
            data: cities
          };
        }
      }
    } catch (apiError) {
      console.log('Amadeus API city search failed, using fallback:', apiError);
    }
    
    // Fallback: return empty or partial matches from popular cities
    return {
      success: true,
      data: []
    };
  } catch (error) {
    console.error('City search error:', error);
    return {
      success: false,
      error: error.message || 'Failed to search cities'
    };
  }
};

/**
 * Search for hotels by city code using Amadeus API
 */
export const searchHotelsByCity = async (cityCode) => {
  try {
    const token = await getAccessToken();
    
    const response = await fetch(`${AMADEUS_BASE_URL}/v1/reference-data/locations/hotels/by-city?cityCode=${encodeURIComponent(cityCode)}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ errors: [{ detail: 'Unknown error' }] }));
      return {
        success: false,
        error: errorData.errors?.[0]?.detail || 'Failed to search hotels by city'
      };
    }

    const data = await response.json();
    
    return {
      success: true,
      data: data.data || []
    };
  } catch (error) {
    console.error('Hotel search by city error:', error);
    return {
      success: false,
      error: error.message || 'Failed to search hotels by city'
    };
  }
};

/**
 * Search for hotels with availability and pricing using Amadeus API
 */
export const searchHotels = async (searchParams) => {
  try {
    const token = await getAccessToken();
    
    // Step 1: Get hotel IDs by city code
    const hotelsListResponse = await fetch(
      `${AMADEUS_BASE_URL}/v1/reference-data/locations/hotels/by-city?cityCode=${encodeURIComponent(searchParams.cityCode)}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!hotelsListResponse.ok) {
      const errorData = await hotelsListResponse.json().catch(() => ({ errors: [{ detail: 'Unknown error' }] }));
      const errorMessage = errorData.errors?.[0]?.detail || 'Failed to find hotels in this city';
      console.error('Amadeus hotels list error:', errorData);
      return {
        success: false,
        error: errorMessage
      };
    }

    const hotelsListData = await hotelsListResponse.json();
    const hotelIds = hotelsListData.data?.map(hotel => hotel.hotelId).slice(0, 20) || []; // Limit to 20 hotels
    
    if (hotelIds.length === 0) {
      return {
        success: true,
        data: []
      };
    }

    // Step 2: Get hotel offers using hotel IDs
    const hotelIdsParam = hotelIds.join(',');
    const params = new URLSearchParams({
      hotelIds: hotelIdsParam,
      checkInDate: searchParams.checkInDate,
      checkOutDate: searchParams.checkOutDate,
      adults: searchParams.adults?.toString() || '1',
      roomQuantity: searchParams.roomQuantity?.toString() || '1',
      currency: searchParams.currency || 'NGN',
    });

    const offersResponse = await fetch(`${AMADEUS_BASE_URL}/v3/shopping/hotel-offers?${params}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!offersResponse.ok) {
      const errorData = await offersResponse.json().catch(() => ({ errors: [{ detail: 'Unknown error' }] }));
      const errorMessage = errorData.errors?.[0]?.detail || errorData.error_description || 'Hotel search failed';
      console.error('Amadeus API error:', errorData);
      return {
        success: false,
        error: errorMessage
      };
    }

    const offersData = await offersResponse.json();
    
    // Log full API response for debugging
    console.log('🏨 Full Amadeus Hotel API Response:', JSON.stringify(offersData, null, 2));
    
    // Transform Amadeus response to our format
    const hotels = offersData.data?.map((hotelData) => {
      const hotel = hotelData.hotel;
      const offers = hotelData.offers || [];
      
      // Log individual hotel data
      console.log('📋 Hotel Data Available:', {
        hotelId: hotel.hotelId,
        name: hotel.name,
        rating: hotel.rating,
        address: hotel.address,
        amenities: hotel.amenities,
        description: hotel.description,
        contact: hotel.contact,
        media: hotel.media,
        hotelDistance: hotelData.hotelDistance,
        offers: offers.length,
        self: hotelData.self,
        type: hotelData.type
      });
      
      return {
        hotelId: hotel.hotelId,
        name: hotel.name,
        rating: hotel.rating || null,
        address: {
          lines: hotel.address?.lines || [],
          cityName: hotel.address?.cityName || '',
          countryCode: hotel.address?.countryCode || '',
          postalCode: hotel.address?.postalCode || '',
          stateCode: hotel.address?.stateCode || '',
        },
        contact: {
          phone: hotel.contact?.phone || null,
          fax: hotel.contact?.fax || null,
          email: hotel.contact?.email || null,
        },
        hotelDistance: hotelData.hotelDistance || null,
        amenities: hotel.amenities || [],
        description: hotel.description?.text || null,
        media: hotel.media || null, // Images and media
        price: offers[0]?.price || null,
        offers: offers.map(offer => ({
          id: offer.id,
          room: offer.room,
          price: offer.price,
          policies: offer.policies,
          self: offer.self,
          type: offer.type,
          checkInDate: offer.checkInDate,
          checkOutDate: offer.checkOutDate,
          roomQuantity: offer.roomQuantity,
          guests: offer.guests,
        })),
        // Additional raw data for reference
        rawData: hotelData, // Keep full raw data for debugging
      };
    }) || [];

    return {
      success: true,
      data: hotels
    };
  } catch (error) {
    console.error('Hotel search error:', error);
    return {
      success: false,
      error: error.message || 'Failed to search hotels. Please try again.'
    };
  }
};

/**
 * Get hotel details by hotel ID using Amadeus API
 */
export const getHotelDetails = async (hotelId, checkInDate, checkOutDate, adults = 1) => {
  try {
    const token = await getAccessToken();
    
    const params = new URLSearchParams({
      hotelIds: hotelId,
      checkInDate,
      checkOutDate,
      adults: adults.toString(),
      currency: 'NGN'
    });

    const response = await fetch(`${AMADEUS_BASE_URL}/v3/shopping/hotel-offers?${params}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ errors: [{ detail: 'Unknown error' }] }));
      return {
        success: false,
        error: errorData.errors?.[0]?.detail || 'Failed to get hotel details'
      };
    }

    const data = await response.json();
    
    if (data.data && data.data.length > 0) {
      const hotelData = data.data[0];
      const hotel = hotelData.hotel;
      
      return {
        success: true,
        data: {
          hotelId: hotel.hotelId,
          name: hotel.name,
          rating: hotel.rating || null,
          address: hotel.address || {},
          amenities: hotel.amenities || [],
          description: hotel.description?.text || null,
          offers: hotelData.offers || [],
        }
      };
    }

    return {
      success: false,
      error: 'Hotel not found'
    };
  } catch (error) {
    console.error('Hotel details error:', error);
    return {
      success: false,
      error: error.message || 'Failed to get hotel details'
    };
  }
};
